import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, DismissibleTag, InlineNotification, Modal, Tag, TextInput, Toggle } from '@carbon/react';
import { Add, Renew } from '@carbon/icons-react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, EmptyState, ErrorState, InlineError, PageHeader } from '../components/states';
import { formatVnd } from '@ruby-cms/shared';
import { Money, When } from '../components/format';
import { MoneyInput } from '../components/MoneyInput';
import { CommandStatus } from '../components/labels';
import type { Command } from './Commands';
import { commandError } from './Player360';
import { pct } from './JackpotBot';

// Founder page (founder 2026-10-07, docs/specs/mkt-dai-ly-reset-quy.md): the MKT agent codes (every account at any depth
// of a listed code's agent tree is an MKT account, docs/specs/mkt-cay-1087.md: its stakes and wins stay out of every game pool) and the pool reset (one game's pool or every
// pool to the founder's reset target, Thông số tiền datLaiQuy.soDuSau, default 0; seeds unchanged). The API refuses every other role.

type Holder = {
  code: string; partner: { id: string; displayName: string; status: string; gameUserId: number } | null; house: boolean;
  total: number; mkt: number; cleared: number; pending: number; todo: number;
  byDepth: { depth: number; agents: number; players: number }[];
};
type Summary = { codes: string[]; version: number; tree: boolean; complete: boolean; tooBig: boolean; holders: Holder[] };
type Sync = { created: number; applied: number; pending: number; on: number; off: number } | null;
// since quy-moi-game (owner 2026-10-12) he A lists every game the pool can govern (coDong false = no pool row yet: it starts at
// the reset target when it first settles), its zone and group; an older he A build sends the first six fields only
type Pool = {
  gid: string; name: string; soDu: number | null; quyBanDau: number; sauReset: number; bat: boolean;
  nhom?: string; trongDanhMuc?: boolean; coDong?: boolean; nguong?: number; dem?: number; vung?: 'do' | 'dem' | 'xanh' | null;
  // task quy-10tr: the most the house loses in one round (0 = no cap); absent on an older he A build
  ganh?: number;
  // task tx-nhan-het: false = the game does not read ganh (Tài Xỉu, MD5); absent on an older he A build = it does
  ganhApDung?: boolean;
  // task tx-nhan-het: true = the house pays past the pool (MD5: its dice are fixed and hashed before any bet)
  traNgoaiQuy?: boolean;
  // task quy-vao-98-cms: the share of real stakes into the pool now in force; null = the game does not use it (Tài Xỉu, MD5)
  tyLeVaoQuy?: number | null;
};
type OutOfScope = { ten: string; lyDo: string };
// nhom game cua danh muc quy (backend config danh_muc_game.json quyGame.nhom)
const NHOM: Record<string, string> = { mini: 'Mini game', slot: 'Slot', ban: 'Bàn trực tiếp', so: 'Xổ số nhanh' };
type PoolData = { pools: Pool[]; resets: Reset[]; outOfScope?: OutOfScope[]; controlVersion?: number };
const VUNG: Record<string, { label: string; type: 'red' | 'warm-gray' | 'green' }> = {
  do: { label: 'Dưới ngưỡng, nhà cái thắng', type: 'red' },
  dem: { label: 'Đang hồi quỹ', type: 'warm-gray' },
  xanh: { label: 'Trên ngưỡng', type: 'green' },
};
// the one balance these pools reset to, or null when the game caps make it differ per pool
const cungMuc = (ps: Pool[]) => (ps.length && ps.every((p) => p.sauReset === ps[0]!.sauReset) ? ps[0]!.sauReset : null);
// muc thua / nguong / phan vao quy (%) cua mot game, hoac gia tri chung cua moi game (khac nhau = rong: de trong thi khong doi)
type Lim = { ganh: string; nguong: string; vao: string };
const limOf = (p: Pool): Lim => ({
  ganh: String(p.ganh ?? 0), nguong: String(p.nguong ?? 0), vao: typeof p.tyLeVaoQuy === 'number' ? String(pct(p.tyLeVaoQuy)) : '',
});
const limChung = (ps: Pool[]): Lim => {
  const chung = (k: keyof Lim, ds: Lim[]) => (ds.length && ds.every((x) => x[k] === ds[0]![k]) ? ds[0]![k] : '');
  const all = ps.map(limOf);
  return { ganh: chung('ganh', all), nguong: chung('nguong', all), vao: chung('vao', ps.filter((p) => typeof p.tyLeVaoQuy === 'number').map(limOf)) };
};
// % nhap -> ty le 0..1, null khi khong hop le
const vaoTyLe = (v: string) => {
  const n = Number(v.trim().replace(',', '.'));
  return v.trim() !== '' && Number.isFinite(n) && n >= 0 && n <= 100 ? n / 100 : null;
};
type ResetRow = { gid: string; name: string; truoc: number | null; sau: number };   // truoc null = game chua co dong quy
type Reset = { id: string; status: string; createdAt: string; completedAt: string | null; maker: string; gid: string | null; error: string | null; rows: ResetRow[] };

const CODE = /^[0-9]{3,12}$/;

export function MktQuyPage() {
  const { me } = useAuth();
  if (me?.role !== 'founder') {
    return <div className="page"><EmptyState title="Chỉ chủ sở hữu dùng trang này" /></div>;
  }
  return (
    <div className="page">
      <PageHeader title="MKT và quỹ"
        lead="Nick thuộc các mã đại lý MKT không đóng góp và không rút tiền từ quỹ game. Mỗi game nhà cái tự quay có một quỹ chặn, bật hoặc tắt ở đây. Reset quỹ đưa quỹ của một game hoặc mọi game về số dư đặt ở Thông số tiền (mặc định 0)." />
      <AgentCodes />
      <PoolReset />
    </div>
  );
}

// ------------------------------------------------------------ MKT agent codes

function AgentCodes() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['mkt-agents'], queryFn: () => api.get<Summary>('/api/admin/mkt-agents') });
  const [draft, setDraft] = useState<string[] | null>(null);
  const [input, setInput] = useState('');
  const [confirm, setConfirm] = useState<'save' | 'sync' | null>(null);
  const [done, setDone] = useState<{ sync: Sync; command?: Command | null } | null>(null);
  const save = useMutation({
    mutationFn: (codes: string[]) => api.put<{ command: Command | null; sync: Sync; summary: Summary }>('/api/admin/mkt-agents', { codes, expect: q.data!.version }),
    onSuccess: (r) => { qc.setQueryData(['mkt-agents'], r.summary); setDraft(null); setConfirm(null); setDone({ sync: r.sync, command: r.command }); },
  });
  const sync = useMutation({
    mutationFn: () => api.post<{ sync: Sync; summary: Summary }>('/api/admin/mkt-agents/sync', {}),
    onSuccess: (r) => { qc.setQueryData(['mkt-agents'], r.summary); setConfirm(null); setDone({ sync: r.sync }); },
  });
  if (q.isLoading) return <section className="section"><BlockSkeleton lines={6} /></section>;
  if (q.error || !q.data) return <section className="section"><ErrorState error={q.error} onRetry={() => q.refetch()} /></section>;
  const codes = draft ?? q.data.codes;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(q.data.codes);
  const bad = input && !CODE.test(input) ? 'Mã đại lý gồm 3 đến 12 chữ số' : codes.includes(input) ? 'Mã đã có trong danh sách' : codes.length >= 20 ? 'Tối đa 20 mã' : null;
  const add = () => { if (input && !bad) { setDraft([...codes, input]); setInput(''); } };
  const todo = q.data.holders.reduce((s, h) => s + h.todo, 0);
  const added = draft ? draft.filter((c) => !q.data.codes.includes(c)) : [];
  const err = commandError(done?.command ?? null);
  return (
    <section className="section">
      <h2>Đại lý MKT</h2>
      <p className="muted note">Mọi nick trong cả cây dưới mã trong danh sách (đại lý con ở mọi cấp và người chơi của họ) là nick MKT: nick đăng ký mới được gắn ngay lúc tạo, nick đã có được gắn khi lưu và tự kiểm lại mỗi phút. Nick rời cây (chuyển sang đại lý ngoài cây, hoặc bỏ mã khỏi danh sách) được bỏ đánh dấu mà danh sách đã gắn; đánh dấu founder tự đặt hay tự bỏ trên trang người chơi thì giữ nguyên.</p>
      {!q.data.tree && q.data.codes.length ? (
        <InlineNotification kind="info" lowContrast hideCloseButton title="Danh sách đang theo cách cũ (chỉ nick trực tiếp)"
          subtitle="Bấm Lưu danh sách một lần để áp dụng cho cả cây." />
      ) : null}
      {!q.data.complete ? (
        <InlineNotification kind="warning" lowContrast hideCloseButton title="Có mã không thuộc đại lý nào hoặc là mã nhà cái"
          subtitle="Trong lúc đó không bỏ đánh dấu nick nào rời cây. Sửa hoặc bỏ mã đó khỏi danh sách." />
      ) : null}
      {q.data.tooBig ? (
        <InlineNotification kind="warning" lowContrast hideCloseButton title="Cây quá lớn để gửi đủ mã cho máy chủ game"
          subtitle="Nick đăng ký dưới mã đại lý mới được gắn MKT ở lần kiểm kế tiếp (tối đa 1 phút), không ngay lúc tạo." />
      ) : null}
      <div className="panel stack mkt-codes">
        <div className="row mkt-codes__tags" aria-label="Mã đại lý MKT">
          {codes.length ? codes.map((c) => (
            <DismissibleTag key={c} type={added.includes(c) ? 'green' : 'gray'} size="md" text={c} title={'Bỏ mã ' + c}
              onClose={() => setDraft(codes.filter((x) => x !== c))} />
          )) : <span className="muted">Chưa có mã nào</span>}
        </div>
        <div className="row row--top">
          <TextInput id="mkt-code" labelText="Thêm mã đại lý" inputMode="numeric" placeholder="Mã số" value={input}
            invalid={!!bad} invalidText={bad ?? ''} onChange={(e) => setInput(e.target.value.replace(/\D/g, '').slice(0, 12))}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
          <Button kind="tertiary" size="md" renderIcon={Add} disabled={!input || !!bad} onClick={add}>Thêm</Button>
        </div>
        {dirty ? (
          <div className="row">
            <Button kind="primary" size="md" onClick={() => setConfirm('save')}>Lưu danh sách</Button>
            <Button kind="ghost" size="md" onClick={() => setDraft(null)}>Bỏ thay đổi</Button>
          </div>
        ) : null}
        {done ? (
          <InlineNotification kind={err ? 'error' : done.sync?.pending ? 'warning' : 'success'} lowContrast onCloseButtonClick={() => setDone(null)}
            title={err ? 'Hệ A chưa nhận danh sách' : done.sync ? `Đã gửi ${done.sync.created} lệnh: gắn MKT ${done.sync.on} nick, bỏ ${done.sync.off} nick rời cây` : 'Đã lưu danh sách'}
            subtitle={err ?? (done.sync?.pending ? `${done.sync.pending} lệnh đang gửi lại, xem lại số liệu sau ít phút.` : undefined)} />
        ) : null}
      </div>
      {q.data.holders.length ? (
        <table className="plain vis mkt-table">
          <thead>
            <tr><th>Mã</th><th>Đại lý đang giữ mã</th><th className="r">Tổng nick</th><th className="r">Đã MKT</th><th className="r">Chưa gắn</th><th className="r">Founder đã bỏ</th></tr>
          </thead>
          <tbody>
            {q.data.holders.map((h) => (
              <tr key={h.code}>
                <td className="is-title num">{h.code}</td>
                <td data-label="Đại lý">{h.partner
                  ? <><Link className="cell-link" to={'/agents/' + h.partner.id}>{h.partner.displayName}</Link>{h.partner.status !== 'ACTIVE' ? <Tag size="sm" type="warm-gray">{h.partner.status}</Tag> : null}</>
                  : <span className="muted">Không có đại lý giữ mã này</span>}{h.house ? <Tag size="sm" type="red">Mã nhà cái</Tag> : null}
                  {h.byDepth.length ? <div className="muted mkt-depth">{h.byDepth.map((d) => `cấp ${d.depth}: ${d.agents} đại lý, ${d.players} người chơi`).join(' · ')}</div> : null}</td>
                <td data-label="Tổng nick" className="r num">{h.total}</td>
                <td data-label="Đã MKT" className="r num">{h.mkt}{h.pending ? <span className="muted"> (+{h.pending} đang gửi)</span> : null}</td>
                <td data-label="Chưa gắn" className={'r num' + (h.todo ? ' num--neg' : '')}>{h.todo}</td>
                <td data-label="Founder đã bỏ" className="r num">{h.cleared}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {todo && !dirty ? (
        <div className="row mkt-sync">
          <Button kind="secondary" size="md" renderIcon={Renew} onClick={() => setConfirm('sync')}>Gắn MKT ngay cho {todo} nick</Button>
        </div>
      ) : null}
      <Modal open={confirm === 'save'} modalHeading="Lưu danh sách mã đại lý MKT?" primaryButtonText="Lưu và gắn MKT" secondaryButtonText="Hủy"
        primaryButtonDisabled={save.isPending} onRequestClose={() => setConfirm(null)} onRequestSubmit={() => save.mutate(codes)}>
        <div className="stack">
          <p>Danh sách mới: {codes.length ? codes.map((c) => <span key={c} className="num">{c} </span>) : 'trống'}.</p>
          <p>Mọi nick trong cả cây dưới các mã này (nick của đại lý và đại lý con ở mọi cấp, người chơi của họ, nick đăng ký bằng mã trong cây mà chưa gắn đại lý) được đánh dấu MKT ngay sau khi lưu; nick mà danh sách đã gắn nay không còn trong cây nào thì được bỏ đánh dấu. Từ ván chốt kế tiếp, tiền cược và tiền thắng của các nick đó không vào quỹ game và không có trong báo cáo doanh thu.</p>
          <InlineError error={save.error} />
        </div>
      </Modal>
      <Modal open={confirm === 'sync'} modalHeading={`Gắn MKT cho ${todo} nick?`} primaryButtonText="Gắn MKT" secondaryButtonText="Hủy"
        primaryButtonDisabled={sync.isPending} onRequestClose={() => setConfirm(null)} onRequestSubmit={() => sync.mutate()}>
        <div className="stack">
          <p>Các nick trong cây dưới mã MKT chưa được đánh dấu sẽ được đánh dấu ngay. Nick founder đã bỏ đánh dấu không bị gắn lại.</p>
          <InlineError error={sync.error} />
        </div>
      </Modal>
    </section>
  );
}

// ------------------------------------------------------------ pool reset

function PoolReset() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['pools'], queryFn: () => api.get<PoolData>('/api/admin/pools') });
  const [sw, setSw] = useState<{ p: Pool; bat: boolean } | null>(null);
  const swM = useMutation({
    mutationFn: (x: { gid: string; bat: boolean }) => api.put<{ command: Command }>('/api/admin/pools/switch', { ...x, expect: q.data?.controlVersion ?? 0 }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pools'] });
      if (r.command.status === 'SUCCEEDED') setSw(null);
    },
  });
  useEffect(() => { if (sw) swM.reset(); }, [sw]); // eslint-disable-line react-hooks/exhaustive-deps
  // muc nha cai thua toi da mot van + nguong bao ve quy + phan cuoc vao quy: mot game (p) hoac moi game quy chan (p null).
  // goc = gia tri luc mo hop thoai; chi gui o da doi (task quy-vao-98-cms: o khong doi khong ghi de gia tri cua game nao)
  // v = phien ban luc mo hop thoai: trang tai lai giua chung thi lenh bi tu choi (409), khong ghi de gia tri moi hon
  const [lim, setLim] = useState<{ p: Pool | null; goc: Lim; v: number } & Lim | null>(null);
  const limM = useMutation({
    mutationFn: (x: { gid: string | null; expect: number; ganhToiDaMoiVan?: number; nguongBaoVeQuy?: number; tyLeVaoQuy?: number }) =>
      api.put<{ command: Command }>('/api/admin/pools/limits', x),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pools'] });
      if (r.command.status === 'SUCCEEDED') setLim(null);
    },
  });
  const moLim = (p: Pool | null) => {
    limM.reset();
    const goc = p ? limOf(p) : limChung((q.data?.pools ?? []).filter((x) => x.trongDanhMuc !== false));
    setLim({ p, goc, v: q.data?.controlVersion ?? 0, ...goc });
  };
  const [target, setTarget] = useState<{ gid: string | null } | null>(null);
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [last, setLast] = useState<Command | null>(null);
  useEffect(() => { if (target) { setKey(crypto.randomUUID()); setLast(null); } }, [target]);
  const m = useMutation({
    mutationFn: (gid: string | null) => api.post<{ command: Command }>('/api/admin/pools/reset', { gid, key }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pools'] });
      if (r.command.status === 'SUCCEEDED') setTarget(null); else setLast(r.command);
    },
  });
  if (q.isLoading) return <section className="section"><BlockSkeleton lines={6} /></section>;
  if (q.error || !q.data) return <section className="section"><ErrorState error={q.error} onRetry={() => q.refetch()} /></section>;
  const { pools, resets, outOfScope = [], controlVersion } = q.data;
  // a game with no pool row yet has nothing to reset
  const resettable = pools.filter((p) => p.coDong !== false);
  // mot game quy chan chua co dong cung reset duoc: may chu dat dong o muc reset (task quy-10tr)
  const chosen = target ? (target.gid === null ? resettable : pools.filter((p) => p.gid === target.gid)) : [];
  const canSwitch = controlVersion != null;
  const nhom = [...new Set(pools.map((p) => p.nhom ?? ''))];
  const swErr = commandError(swM.data?.command ?? null);
  const limErr = commandError(limM.data?.command ?? null);
  const coGanh = pools.some((p) => p.ganh != null);
  const coVao = pools.some((p) => p.tyLeVaoQuy !== undefined);
  const cot = 7 + (coGanh ? 1 : 0) + (coVao ? 1 : 0);
  // cac o da doi cua hop thoai muc quy, phan vao quy sai = loi
  const doi = lim ? {
    ...(lim.ganh !== '' && lim.ganh !== lim.goc.ganh ? { ganhToiDaMoiVan: Number(lim.ganh) } : {}),
    ...(lim.nguong !== '' && lim.nguong !== lim.goc.nguong ? { nguongBaoVeQuy: Number(lim.nguong) } : {}),
    ...(lim.vao !== '' && lim.vao !== lim.goc.vao && vaoTyLe(lim.vao) != null ? { tyLeVaoQuy: vaoTyLe(lim.vao)! } : {}),
  } : {};
  const vaoSai = !!lim && lim.vao !== '' && vaoTyLe(lim.vao) == null;
  const limGame = lim?.p ? lim.p.name : `cả ${pools.filter((p) => p.trongDanhMuc !== false).length} game quỹ chặn`;
  const err = commandError(last);
  const muc = cungMuc(resettable);
  const mucChon = cungMuc(chosen);
  const ve = mucChon == null ? 'số dư sau reset' : formatVnd(mucChon);
  return (
    <section className="section">
      <div className="row pool-head">
        <h2>Quỹ chặn từng game</h2>
        <div className="row pool-head__act">
          {muc != null ? <span className="pool-target">Reset về <Money v={muc} /> <Link className="cell-link" to="/settings/tham-so">Đổi mức</Link></span> : null}
          {coGanh && canSwitch ? <Button kind="tertiary" size="md" onClick={() => moLim(null)}>Đặt mức cho mọi game</Button> : null}
          {resettable.length > 1 ? <Button kind="danger--tertiary" size="md" onClick={() => setTarget({ gid: null })}>Reset tất cả quỹ</Button> : null}
        </div>
      </div>
      <p className="muted note">Quỹ chặn bật: tiền người thật trả ra lấy từ quỹ của game, quỹ không trả quá số dư (trừ Tài Xỉu MD5: kết quả khóa bằng mã MD5 trước khi cược nên quỹ không điều được, phần vượt quỹ nhà cái trả, quỹ về 0); mỗi ván nhà cái thua người thật không quá mức "Thua tối đa / ván" (0 = không giới hạn; Tài Xỉu và Tài Xỉu MD5 không dùng mức này: nhận mọi cược hợp lệ, Tài Xỉu cho cửa đông tiền thua khi quỹ không đủ trả phần lệch, MD5 nhà cái trả cả phần vượt quỹ); "Vào quỹ" là phần tiền cược người thật vào quỹ, phần còn lại là lời nhà cái (Tài Xỉu nạp quỹ bằng phí nên không dùng); dưới ngưỡng thì nhà cái thắng các ván có người thật cho tới khi cược nạp quỹ lại (ngưỡng 0 = không có vùng này). Tắt: kết quả ngẫu nhiên tự nhiên. Game mới bật chưa có quỹ bắt đầu từ mức Reset quỹ. Reset đưa số dư về mức đặt ở Thông số tiền, mức gốc không đổi; máy chủ sao lưu file quỹ trước và ghi mỗi quỹ một dòng nhật ký.</p>
      {pools.length ? (
        <table className="plain vis pool-table">
          <thead>
            <tr><th>Game</th><th>Quỹ chặn</th><th>Vùng quỹ</th>{coGanh ? <th className="r">Thua tối đa / ván</th> : null}{coVao ? <th className="r">Vào quỹ</th> : null}<th className="r">Số dư hiện tại</th><th className="r">Sau reset</th><th className="r">Chênh lệch</th><th aria-label="Thao tác" /></tr>
          </thead>
          {nhom.map((n) => (
            <tbody key={n || '-'}>
              {n ? <tr className="pool-group"><th colSpan={cot} scope="rowgroup">{NHOM[n] ?? n}</th></tr> : null}
              {pools.filter((p) => (p.nhom ?? '') === n).map((p) => {
                const coDong = p.coDong !== false;
                const diff = p.soDu == null || !coDong ? null : p.sauReset - p.soDu;
                const v = p.bat && p.vung ? VUNG[p.vung] : null;
                return (
                  <tr key={p.gid} className={p.bat ? undefined : 'is-off'}>
                    <td className="is-title"><div className="vis__name">{p.name}</div><div className="muted num">{p.gid}</div></td>
                    <td data-label="Quỹ chặn">
                      <Toggle id={'quy-' + p.gid} size="sm" labelText="" aria-label={'Quỹ chặn ' + p.name} labelA="Tắt" labelB="Bật" toggled={p.bat}
                        disabled={!canSwitch || p.trongDanhMuc === false} onToggle={(bat) => setSw({ p, bat })} />
                    </td>
                    <td data-label="Vùng quỹ"><div>
                      {v ? <Tag size="sm" type={v.type}>{v.label}</Tag> : <span className="muted">{p.bat ? '' : 'Kết quả tự nhiên'}</span>}
                      {p.bat && p.nguong != null ? <div className="muted pool-nguong">Ngưỡng <Money v={p.nguong} /></div> : null}
                    </div></td>
                    {coGanh ? (
                      <td data-label="Thua tối đa / ván" className="r"><div>
                        {p.ganhApDung === false ? <span className="muted">{p.traNgoaiQuy ? 'Không áp dụng (nhận hết, nhà cái trả cả phần vượt quỹ)' : 'Không áp dụng (nhận hết, quỹ điều)'}</span>
                          : p.ganh ? <Money v={p.ganh} /> : <span className="muted">Không giới hạn</span>}
                        {p.trongDanhMuc !== false && canSwitch ? <div><Button kind="ghost" size="sm" onClick={() => moLim(p)}>Sửa</Button></div> : null}
                      </div></td>
                    ) : null}
                    {coVao ? (
                      <td data-label="Vào quỹ" className="r">
                        {typeof p.tyLeVaoQuy === 'number' ? <span className="num">{pct(p.tyLeVaoQuy)}%</span> : <span className="muted">Không dùng</span>}
                      </td>
                    ) : null}
                    <td data-label="Số dư hiện tại" className="r"><div>
                      {p.soDu == null ? <span className="num--neg">không đọc được</span> : <Money v={p.soDu} />}
                      {coDong ? null : <div className="muted pool-nguong">chưa có ván nào</div>}
                    </div></td>
                    <td data-label="Sau reset" className="r">{coDong ? <Money v={p.sauReset} /> : null}</td>
                    <td data-label="Chênh lệch" className="r">{diff == null ? null : diff === 0 ? <span className="muted">đã bằng</span> : <Money v={diff} signed />}</td>
                    <td className="r">{coDong || p.trongDanhMuc ? <Button kind="danger--ghost" size="sm" onClick={() => setTarget({ gid: p.gid })}>Reset</Button> : null}</td>
                  </tr>
                );
              })}
            </tbody>
          ))}
        </table>
      ) : <EmptyState title="Chưa có quỹ game nào" />}

      {outOfScope.length ? (
        <>
          <h3 className="pool-log__title">Không chặn được bằng quỹ</h3>
          <table className="plain pool-out">
            <thead><tr><th>Game</th><th>Lý do</th></tr></thead>
            <tbody>
              {outOfScope.map((g) => <tr key={g.ten}><td className="is-title"><div className="vis__name">{g.ten}</div></td><td data-label="Lý do">{g.lyDo}</td></tr>)}
            </tbody>
          </table>
        </>
      ) : null}

      <Modal open={!!sw} danger={!!sw && !sw.bat} modalHeading={sw ? `${sw.bat ? 'Bật' : 'Tắt'} quỹ chặn ${sw.p.name}?` : ''}
        primaryButtonText={swM.isPending ? 'Đang gửi...' : sw?.bat ? 'Bật quỹ chặn' : 'Tắt quỹ chặn'} secondaryButtonText="Hủy"
        primaryButtonDisabled={swM.isPending} onRequestClose={() => setSw(null)} onRequestSubmit={() => sw && swM.mutate({ gid: sw.p.gid, bat: sw.bat })}>
        <div className="stack">
          {sw?.bat ? (
            <p>Từ ván sau, tiền trả người thật của {sw.p.name} lấy từ quỹ của game và không vượt số dư quỹ; dưới ngưỡng thì nhà cái thắng các ván có người thật.
              {sw.p.coDong === false ? <> Game chưa có quỹ: quỹ bắt đầu từ <Money v={sw.p.sauReset} />.</> : null}</p>
          ) : (
            <p>Từ ván sau, {sw?.p.name} quay kết quả tự nhiên, không còn quỹ chặn: tiền trả người thật không bị giới hạn bởi quỹ. Số dư quỹ giữ nguyên.</p>
          )}
          <p className="muted">Máy chủ áp dụng ngay, không chờ người duyệt.</p>
          {swErr ? <p className="text-error">{swErr}</p> : null}
          <InlineError error={swM.error} />
        </div>
      </Modal>

      <Modal open={!!lim} modalHeading={`Mức thua, ngưỡng và phần vào quỹ: ${limGame}`}
        primaryButtonText={limM.isPending ? 'Đang gửi...' : 'Lưu'} secondaryButtonText="Hủy"
        primaryButtonDisabled={limM.isPending || !lim || vaoSai || !Object.keys(doi).length}
        onRequestClose={() => setLim(null)}
        onRequestSubmit={() => lim && Object.keys(doi).length && !vaoSai && limM.mutate({ gid: lim.p?.gid ?? null, expect: lim.v, ...doi })}>
        {lim ? (
          <div className="stack">
            <MoneyInput id="lim-ganh" labelText="Nhà cái thua tối đa mỗi ván (0 = không giới hạn)" value={lim.ganh} maxDigits={13}
              onChange={(d) => setLim({ ...lim, ganh: d })} />
            {lim.p && lim.p.ganhApDung === false ? (
              <p className="muted">{lim.p.name} không dùng mức thua tối đa mỗi ván: nhận mọi cược hợp lệ, {lim.p.traNgoaiQuy
                ? 'kết quả khóa bằng mã MD5 trước khi cược nên quỹ không điều được, phần vượt quỹ nhà cái trả.'
                : 'quỹ điều kết quả (cửa đông tiền thua khi quỹ không đủ trả phần lệch).'}</p>
            ) : null}
            <MoneyInput id="lim-nguong" labelText="Ngưỡng bảo vệ quỹ (0 = không có vùng nhà cái luôn thắng)" value={lim.nguong} maxDigits={13}
              onChange={(d) => setLim({ ...lim, nguong: d })} />
            {lim.p && typeof lim.p.tyLeVaoQuy !== 'number' ? (
              <p className="muted">{lim.p.name} không dùng phần cược vào quỹ: quỹ nạp bằng phí (Thông số tiền).</p>
            ) : (
              <TextInput id="lim-vao" labelText="Phần cược người thật vào quỹ (%), phần còn lại là lời nhà cái" inputMode="decimal" value={lim.vao}
                invalid={vaoSai} invalidText="Nhập từ 0 đến 100" onChange={(e) => setLim({ ...lim, vao: e.target.value })} />
            )}
            {lim.p ? null : <p className="muted">Ô để trống thì giữ nguyên giá trị riêng của từng game. Tài Xỉu và Tài Xỉu MD5 không dùng phần vào quỹ và mức thua tối đa mỗi ván.</p>}
            <p>Từ ván sau, {limGame}:{' '}
              {doi.ganhToiDaMoiVan !== undefined ? <>tiền trả người thật trừ tiền cược của họ trong một ván không quá {formatVnd(doi.ganhToiDaMoiVan)}{doi.ganhToiDaMoiVan ? '' : ' (không giới hạn, chỉ theo quỹ)'}{lim.p && lim.p.ganhApDung === false ? ' (game này không dùng mức này)' : lim.p ? '' : ' (trừ Tài Xỉu và Tài Xỉu MD5)'}. </> : null}
              {doi.nguongBaoVeQuy !== undefined ? (doi.nguongBaoVeQuy ? <>Quỹ dưới {formatVnd(doi.nguongBaoVeQuy)} thì nhà cái thắng mọi ván có người thật. </> : 'Không có vùng nhà cái luôn thắng: người thật thắng được cho tới khi quỹ hết. ') : null}
              {doi.tyLeVaoQuy !== undefined ? <>{pct(doi.tyLeVaoQuy)}% tiền cược người thật vào quỹ, {Math.round((100 - pct(doi.tyLeVaoQuy)) * 10) / 10}% là lời nhà cái{lim.p ? '' : ` (${pools.filter((p) => p.trongDanhMuc !== false && typeof p.tyLeVaoQuy === 'number').length} game, trừ Tài Xỉu và Tài Xỉu MD5)`}. </> : null}
              {Object.keys(doi).length ? null : 'chưa đổi gì.'}</p>
            <p className="muted">Số dư quỹ không đổi. Máy chủ áp dụng ngay, không chờ người duyệt.</p>
            {limErr ? <p className="text-error">{limErr}</p> : null}
            <InlineError error={limM.error} />
          </div>
        ) : null}
      </Modal>

      <h3 className="pool-log__title">Nhật ký reset</h3>
      {resets.length ? (
        <ul className="list pool-log">
          {resets.map((r) => (
            <li key={r.id} className="pool-log__item">
              <div className="row">
                <CommandStatus s={r.status} />
                <span>{r.gid === null ? 'Tất cả quỹ' : r.rows[0]?.name ?? r.gid}</span>
                <span className="muted">bởi {r.maker}, <When v={r.createdAt} /></span>
                <Link className="cell-link" to={'/commands/' + r.id}>Xem lệnh</Link>
              </div>
              {r.rows.length ? (
                <ul className="list pool-log__rows">
                  {r.rows.map((x) => <li key={x.gid}><span>{x.name}</span> <Money v={x.truoc} /> <span aria-hidden>→</span> <Money v={x.sau} /></li>)}
                </ul>
              ) : r.status === 'SUCCEEDED' ? <p className="muted">Không có quỹ nào phải đổi.</p> : null}
              {r.error ? <p className="text-error">{r.error}</p> : null}
            </li>
          ))}
        </ul>
      ) : <p className="muted">Chưa reset lần nào.</p>}

      <Modal open={!!target} danger modalHeading={target?.gid === null ? `Reset tất cả quỹ về ${ve}?` : `Reset quỹ ${chosen[0]?.name ?? ''} về ${ve}?`}
        primaryButtonText={m.isPending ? 'Đang reset...' : 'Reset quỹ'} secondaryButtonText="Hủy" primaryButtonDisabled={m.isPending || !chosen.length}
        onRequestClose={() => setTarget(null)} onRequestSubmit={() => m.mutate(target!.gid)}>
        <div className="stack">
          <table className="plain">
            <thead><tr><th>Game</th><th className="r">Trước</th><th className="r">Sau</th></tr></thead>
            <tbody>
              {chosen.map((p) => (
                <tr key={p.gid}><td>{p.name}</td><td className="r">{p.soDu == null ? '?' : <Money v={p.soDu} />}</td><td className="r"><Money v={p.sauReset} /></td></tr>
              ))}
            </tbody>
          </table>
          <p className="muted">Số "Trước" là lúc mở hộp này; ván chốt trong lúc chờ làm số thật lệch một chút, nhật ký ghi số thật. Ván đang chạy chốt trên số dư mới. Quỹ dưới ngưỡng bảo vệ thì điều khiển chọn kết quả nhà cái thắng người chơi thật cho tới khi cược nạp quỹ lại qua ngưỡng. Không hoàn tác được từ trang này; bản sao file quỹ nằm trên máy chủ.</p>
          {err ? <p className="text-error">{err}</p> : null}
          <InlineError error={m.error} />
        </div>
      </Modal>
    </section>
  );
}
